import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IExtendedCustomerBPA, BpaPaymentSystem, CardBrands } from 'types';
import { bpaPaymentSystem } from 'appConstants';
import { FieldData } from '../../../InputFields';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import { SetName } from './components/SetName';
import { RecipientAccountNumber } from './components/RecipientAccountsNumber';
import { RecipientName } from './components/RecipientName';
import { RecipientBank } from './components/RecipientBank';
import { RecipientBankCity } from './components/RecipientBankCity';
import { RecipientInn } from './components/RecipientInn';
import { RecipientBic } from './components/RecipientBic';
import { RecipientCorrespondentAccount } from './components/RecipientCorrespondentAccount';
import { PaymentDetails } from './components/PaymentDetails';
import { CardContractNumber } from './components/CardContractNumber';
import { SbpContractNumber } from './components/SbpContractNumber';
import { Tariffs } from './components/Tariffs/Tariffs';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import style from './BpaFields.module.scss';

interface IProps {
	bpaSet: IExtendedCustomerBPA;
	allFields: FieldData;
	deleteBpaBlock: (setId: string) => void;
	toggleIsOpenBpaBlock: (setId: string) => void;
	changeName: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientAccountNumber: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientName: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientBank: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientBankCity: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientInn: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientBic: ({ setId, value }: { setId: string; value: string }) => void;
	changeRecipientCorrespondentAccount: ({ setId, value }: { setId: string; value: string }) => void;
	changePaymentDetails: ({ setId, value }: { setId: string; value: string }) => void;
	changeCardContractNumber: ({ setId, value }: { setId: string; value: string }) => void;
	changeSbpContractNumber: ({ setId, value }: { setId: string; value: string }) => void;
	addTariff: (setId: string) => void;
	deleteTariff: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	toggleIsOpenTariff: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	addCardBrand: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	deleteCardBrand: ({ setId, tariffId, brandId }: { setId: string; tariffId: string; brandId: string }) => void;
	changeBpaPaymentSystem: ({
		setId,
		tariffId,
		value,
	}: {
		setId: string;
		tariffId: string;
		value: BpaPaymentSystem;
	}) => void;
	changeCardBrand: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: CardBrands;
	}) => void;
	changeBankFee: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}) => void;
	changeKMFee: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}) => void;
}

export const BpaFields: React.FC<IProps> = ({
	bpaSet,
	allFields,
	deleteBpaBlock,
	toggleIsOpenBpaBlock,
	changeName,
	changeRecipientAccountNumber,
	changeRecipientName,
	changeRecipientBank,
	changeRecipientBankCity,
	changeRecipientInn,
	changeRecipientBic,
	changeRecipientCorrespondentAccount,
	changePaymentDetails,
	changeCardContractNumber,
	changeSbpContractNumber,
	addTariff,
	deleteTariff,
	toggleIsOpenTariff,
	addCardBrand,
	deleteCardBrand,
	changeBpaPaymentSystem,
	changeCardBrand,
	changeBankFee,
	changeKMFee,
}) => {
	const [hasFieldErrors, setHasFieldErrors] = useState(false);
	const expandButtonClasses = classNames(style.expandButton, { [style.buttonError]: hasFieldErrors });
	const expandIconClasses = classNames(style.expandIcon, { [style.rotated]: bpaSet.isOpenSet });
	const expandFieldsClasses = classNames({ [style.collapsed]: !bpaSet.isOpenSet });

	const fieldKeys = useMemo(() => {
		const bpaSetKeys = Object.keys(bpaSet).map((key) => `${key}-${bpaSet.id}`);

		const nestedValues = bpaSet.tariffs.map((extendedTariff) => {
			const tariffKeys = Object.keys(extendedTariff).map((key) => `${key}-${extendedTariff.id}`);
			const brandKeys = extendedTariff.data.map((brands) =>
				Object.keys(brands).map((key) => `${key}-${brands.id}`),
			);

			return [...tariffKeys, ...brandKeys.flat()];
		});

		return [...bpaSetKeys, ...nestedValues.flat()];
	}, [bpaSet]);

	useEffect(() => {
		const hasError = allFields.some((allField) =>
			allField.errors?.length ? fieldKeys.includes(allField.name.toString()) : false,
		);
		setHasFieldErrors(hasError);
	}, [allFields, fieldKeys]);

	const handleDeleteBlock = useCallback(() => {
		deleteBpaBlock(bpaSet.id);
	}, [bpaSet.id, deleteBpaBlock]);

	const handleToggleIsOpenBpaBlock = useCallback(() => {
		toggleIsOpenBpaBlock(bpaSet.id);
	}, [bpaSet.id, toggleIsOpenBpaBlock]);

	const handleChangeName = useCallback(
		(value: string) => {
			changeName({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeName],
	);

	const handleChangeRecipientAccountNumber = useCallback(
		(value: string) => {
			changeRecipientAccountNumber({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientAccountNumber],
	);

	const handleChangeRecipientName = useCallback(
		(value: string) => {
			changeRecipientName({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientName],
	);

	const handleChangeRecipientBank = useCallback(
		(value: string) => {
			changeRecipientBank({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientBank],
	);

	const handleChangeRecipientBankCity = useCallback(
		(value: string) => {
			changeRecipientBankCity({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientBankCity],
	);

	const handleChangeRecipientInn = useCallback(
		(value: string) => {
			changeRecipientInn({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientInn],
	);

	const handleChangeRecipientBic = useCallback(
		(value: string) => {
			changeRecipientBic({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientBic],
	);

	const handleChangeRecipientCorrespondentAccount = useCallback(
		(value: string) => {
			changeRecipientCorrespondentAccount({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeRecipientCorrespondentAccount],
	);

	const handleChangePaymentDetails = useCallback(
		(value: string) => {
			changePaymentDetails({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changePaymentDetails],
	);

	const handleChangeCardContractNumber = useCallback(
		(value: string) => {
			changeCardContractNumber({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeCardContractNumber],
	);

	const handleChangeSbpContractNumber = useCallback(
		(value: string) => {
			changeSbpContractNumber({ setId: bpaSet.id, value });
		},
		[bpaSet.id, changeSbpContractNumber],
	);

	const handleAddTariff = useCallback(() => {
		addTariff(bpaSet.id);
	}, [bpaSet.id, addTariff]);

	return (
		<div className={style.container}>
			<div className={style.fields}>
				<Button
					className={expandButtonClasses}
					theme="secondaryPurple"
					onClick={handleToggleIsOpenBpaBlock}
					iconPosition="post"
					icon={
						<svg className={expandIconClasses}>
							<use xlinkHref={`${breadcrumbs}#rightAngleArrow`} />
						</svg>
					}>
					<Text translationNameSpace="form" translationKey={bpaSet.name} asFragment={true} />
				</Button>
				<div className={expandFieldsClasses}>
					<div className={style.row}>
						<SetName setId={bpaSet.id} onChange={handleChangeName} />
						<div className={style.flex1} />
					</div>
					<Text
						translationNameSpace="form"
						translationKey="requisitesHeading"
						theme="secondary"
						size="s"
						weight="bold"
						display="inline"
						className={style.bottomMargin}
					/>
					<div className={style.row}>
						<RecipientAccountNumber setId={bpaSet.id} onChange={handleChangeRecipientAccountNumber} />
						<RecipientName setId={bpaSet.id} onChange={handleChangeRecipientName} />
					</div>
					<div className={style.row}>
						<RecipientBank setId={bpaSet.id} onChange={handleChangeRecipientBank} />
						<RecipientBankCity setId={bpaSet.id} onChange={handleChangeRecipientBankCity} />
					</div>
					<div className={style.row}>
						<RecipientInn setId={bpaSet.id} onChange={handleChangeRecipientInn} />
						<RecipientBic setId={bpaSet.id} onChange={handleChangeRecipientBic} />
					</div>
					<div className={style.row}>
						<RecipientCorrespondentAccount
							setId={bpaSet.id}
							onChange={handleChangeRecipientCorrespondentAccount}
						/>
						<PaymentDetails setId={bpaSet.id} onChange={handleChangePaymentDetails} />
					</div>
					<div className={style.row}>
						<CardContractNumber setId={bpaSet.id} onChange={handleChangeCardContractNumber} />
						<SbpContractNumber setId={bpaSet.id} onChange={handleChangeSbpContractNumber} />
					</div>
					<div className={style.tariffs}>
						<Text
							translationNameSpace="form"
							translationKey="tariffHeading"
							theme="secondary"
							size="s"
							weight="bold"
							display="inline"
						/>
						<Button
							className={style.button}
							theme="primary"
							iconPosition="post"
							disabled={bpaSet.tariffs.length >= bpaPaymentSystem.length}
							onClick={handleAddTariff}
							icon={
								<svg className={style.iconPlus}>
									<use xlinkHref={`${breadcrumbs}#plus`} />
								</svg>
							}>
							<Text translationNameSpace="form" translationKey="addButton" asFragment={true} />
						</Button>
					</div>
					{bpaSet.tariffs.map((tariff) => (
						<Tariffs
							key={tariff.id}
							setId={bpaSet.id}
							tariff={tariff}
							tariffs={bpaSet.tariffs}
							deleteTariff={deleteTariff}
							toggleIsOpenTariff={toggleIsOpenTariff}
							addCardBrand={addCardBrand}
							deleteCardBrand={deleteCardBrand}
							changeBpaPaymentSystem={changeBpaPaymentSystem}
							changeCardBrand={changeCardBrand}
							changeBankFee={changeBankFee}
							changeKMFee={changeKMFee}
						/>
					))}
				</div>
				<div className={style.deleteBtnWrapper}>
					<Button
						className={style.button}
						theme="secondaryPurple"
						iconPosition="pre"
						onClick={handleDeleteBlock}
						icon={
							<svg className={style.iconBin}>
								<use xlinkHref={`${breadcrumbs}#bin`} />
							</svg>
						}>
						<Text translationNameSpace="form" translationKey="deleteSetButton" asFragment={true} />
					</Button>
				</div>
			</div>
			<div className={style.divider} />
		</div>
	);
};
